import { AxiosInstance } from "axios";
import { ApiRouteHelpers as Helpers } from "../../../root/apiRoutes";
import {
  CreatePrivacyCenterNoticeRequest,
  NoticeEditorMetadataResponse,
  PrivacyCenterNoticeDto,
  UpdatePrivacyCenterNoticeRequest,
} from "../dto/PrivacyCenterDto";

import {
  OrganizationPublicId,
  PrivacyCenterNoticePublicId,
  PrivacyCenterPublicId,
  OkResponse,
} from "../Types";
import { PrivacyCenterNoticeSectionController } from "./PrivacyCenterNoticeSectionController";

export class PrivacyCenterNoticeController {
  constructor(private axios: AxiosInstance) {}

  public getNotices = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
  ) => {
    if (!organizationId || !privacyCenterId) return [];
    const url = Helpers.organization.privacyCenter.notices.root(organizationId, privacyCenterId);
    const response = await this.axios.get<PrivacyCenterNoticeDto[]>(url);
    return response.data;
  };

  public getNotice = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    noticeId: PrivacyCenterNoticePublicId,
  ) => {
    const url = Helpers.organization.privacyCenter.notices.specific(
      organizationId,
      privacyCenterId,
      noticeId,
    );
    const response = await this.axios.get<PrivacyCenterNoticeDto>(url);
    return response.data;
  };

  public createNotice = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    noticeBody: CreatePrivacyCenterNoticeRequest,
  ) => {
    const url = Helpers.organization.privacyCenter.notices.root(organizationId, privacyCenterId);
    const response = await this.axios.post<PrivacyCenterNoticeDto>(url, noticeBody);
    return response.data;
  };

  public updateNotice = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    noticeId: PrivacyCenterNoticePublicId,
    updateNoticeBody: UpdatePrivacyCenterNoticeRequest,
  ) => {
    const url = Helpers.organization.privacyCenter.notices.specific(
      organizationId,
      privacyCenterId,
      noticeId,
    );
    const response = await this.axios.patch<PrivacyCenterNoticeDto>(url, updateNoticeBody);
    return response.data;
  };

  public deleteNotice = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    noticeId: PrivacyCenterNoticePublicId,
  ) => {
    const url = Helpers.organization.privacyCenter.notices.specific(
      organizationId,
      privacyCenterId,
      noticeId,
    );
    const response = await this.axios.delete<PrivacyCenterNoticeDto>(url);
    return response.data;
  };
  public sections = new PrivacyCenterNoticeSectionController(this.axios);

  public updateStateSectionVisibility = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    noticeId: PrivacyCenterNoticePublicId,
    stateSectionAnchor: string,
  ) => {
    const url =
      Helpers.organization.privacyCenter.notices.stateSectionVisibility(
        organizationId,
        privacyCenterId,
        noticeId,
      ) + `?section=${stateSectionAnchor}`;
    const response = await this.axios.patch<OkResponse>(url);
    return response.data;
  };

  public getNoticeEditorMetadata = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
  ) => {
    const url = Helpers.organization.privacyCenter.notices.noticeEditorMetadata(
      organizationId,
      privacyCenterId,
    );
    const response = await this.axios.get<NoticeEditorMetadataResponse>(url);
    return response.data;
  };

  public createEditableStateSection = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    noticeId: PrivacyCenterNoticePublicId,
    stateSectionAnchor: string,
  ) => {
    const url =
      Helpers.organization.privacyCenter.notices.createEditableStateSection(
        organizationId,
        privacyCenterId,
        noticeId,
      ) + `?section=${stateSectionAnchor}`;
    const response = await this.axios.patch<OkResponse>(url);
    return response.data;
  };

  public removeEditableStateSection = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    noticeId: PrivacyCenterNoticePublicId,
    stateSectionAnchor: string,
  ) => {
    const url =
      Helpers.organization.privacyCenter.notices.removeEditableStateSection(
        organizationId,
        privacyCenterId,
        noticeId,
      ) + `?section=${stateSectionAnchor}`;
    const response = await this.axios.patch<OkResponse>(url);
    return response.data;
  };

  public generateRedlinesDiff = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    versionNameA: string,
    versionNameB: string,
  ): Promise<string> => {
    const url = Helpers.organization.privacyCenter.notices.redlinesDiff(
      organizationId,
      privacyCenterId,
    );
    const response = await this.axios.post<string>(
      url,
      {
        versionNameA,
        versionNameB,
      },
      {
        responseType: "text",
        transformResponse: [(data) => data],
      },
    );
    return response.data;
  };
}
